import { useEffect, useMemo, useState } from 'react';
import axiosConn from '../../axiosConn';
import HitLabListenerProofSection from './HitLabListenerProofSection';

const PROOF_CARDS = [
  {
    key: 'radioRotation',
    kicker: 'ROTATION',
    title: 'Radio Rotation is live',
    activeBody:
      'Rights-clean records are already active inside Joe Galaxy Radio Rotation.',
    fallbackBody:
      'Rights-clean records can move into Joe Galaxy Radio Rotation after review.',
    countLabel: 'records in rotation',
  },
  {
    key: 'listening',
    kicker: 'LISTENING',
    title: 'Qualified plays',
    activeBody:
      'A play only counts after real time with the record - at least 30 seconds or 25%.',
    fallbackBody:
      'JG Radio tracks listening activity through conservative public proof signals.',
    countLabel: 'qualified plays',
    fallbackCountLabel: 'play signals',
    tooltip:
      'JG Radio can show total qualified plays after real time with the record. Unique daily play signals remain a conservative trust layer; deeper replay behavior should be tracked separately as artist signal.',
  },
  {
    key: 'feedback',
    kicker: 'FEEDBACK',
    title: 'Feedback is structured',
    activeBody:
      'Listeners can leave feedback after they spend time with a record. Praise is not required. Honest reaction is the point.',
    fallbackBody:
      'Listener reaction is collected through structured signal, not loose public comments.',
    countLabel: 'feedback signals submitted',
  },
  {
    key: 'support',
    kicker: 'SUPPORT',
    title: 'Artist support is wired in',
    activeBody:
      'When a listener is feeling a record, they can show love with JGK artist support right inside JG Radio - a tip-style support action with a USD cash-out value for eligible artists.',
    actionBody:
      'When a listener is feeling a record, they can show love with JGK artist support right inside JG Radio - a tip-style support action with a USD cash-out value for eligible artists.',
    fallbackBody:
      'When a listener is feeling a record, they can show love with JGK artist support right inside JG Radio - a tip-style support action with a USD cash-out value for eligible artists.',
    countLabel: 'artist support actions',
  },
];

const getProofData = (response) => response?.data?.data || null;

const getDisplayCount = (value) => {
  const count = Number(value);
  if (!Number.isFinite(count) || count <= 0) return null;
  return Math.round(count);
};

const cleanDisplayText = (value) => String(value || '').trim();

const getSupportAmountProof = (proof) => {
  const support = proof?.support || {};
  if (!support.displaySupportAmount) return null;
  const jgk = cleanDisplayText(support.supportJgkDisplay);
  const usd = cleanDisplayText(support.supportUsdDisplay);
  return jgk && usd ? { jgk, usd } : null;
};

const hasCardSignal = (cardKey, proof) => {
  if (cardKey === 'radioRotation') {
    return Boolean(proof?.radioRotation?.hasActiveRotation);
  }
  if (cardKey === 'listening') {
    return Boolean(proof?.listening?.hasCountedPlays);
  }
  if (cardKey === 'feedback') {
    return Boolean(proof?.feedback?.hasFeedbackSignal);
  }
  if (cardKey === 'support') {
    return Boolean(proof?.support?.hasSupportReady || proof?.support?.hasSupportActions);
  }
  return false;
};

const hasProofActivity = (proof) =>
  PROOF_CARDS.some((card) => hasCardSignal(card.key, proof));

const resolveCardTitle = ({ card, proof }) => {
  if (card.key !== 'support') return card.title;
  if (getSupportAmountProof(proof) || proof?.support?.hasSupportActions) {
    return 'Listeners are showing artist support';
  }
  return card.title;
};

const resolveCardBody = ({ card, proof }) => {
  if (card.key === 'support') {
    if (getSupportAmountProof(proof)) {
      return card.activeBody;
    }
    if (proof?.support?.hasSupportActions) return card.actionBody;
    return card.fallbackBody;
  }
  if (!hasCardSignal(card.key, proof)) return card.fallbackBody;
  if (card.key !== 'listening') return card.activeBody;
  return proof?.listening?.thresholdQualified === true
    ? card.activeBody
    : card.fallbackBody;
};

const resolveCardCount = ({ card, proof, displayCount }) => {
  if (card.key === 'support') {
    const supportAmount = getSupportAmountProof(proof);
    if (supportAmount) {
      return `${supportAmount.jgk} sent as support (${supportAmount.usd})`;
    }
  }
  if (!displayCount) return '';
  if (card.key === 'listening' && proof?.listening?.thresholdQualified !== true) {
    return `${displayCount.toLocaleString()}+ ${card.fallbackCountLabel}`;
  }
  return `${displayCount.toLocaleString()}+ ${card.countLabel}`;
};

export default function HitLabProofSignalSection({
  enabled,
  onStartIntake,
  onOpenRadio,
  voicePreview,
  ctaLabel = 'Put My Record In The Lab',
}) {
  const [proof, setProof] = useState(null);

  useEffect(() => {
    let active = true;

    const loadProof = async () => {
      try {
        const response = await axiosConn.get('hitlab/public-proof');
        if (active) setProof(getProofData(response));
      } catch {
        if (active) setProof(null);
      }
    };

    void loadProof();
    return () => {
      active = false;
    };
  }, []);

  const activeCopy = hasProofActivity(proof);
  const countByCard = useMemo(
    () => ({
      radioRotation: getDisplayCount(proof?.radioRotation?.displayCount),
      listening: getDisplayCount(proof?.listening?.displayCount),
      feedback: getDisplayCount(proof?.feedback?.displayCount),
      support: getDisplayCount(proof?.support?.displayCount),
    }),
    [proof],
  );

  return (
    <section
      className="hitlab-shell hitlab-proof-signal-section"
      aria-label="JG HitLab public proof signals"
    >
      <div className="hitlab-proof-signal-header">
        <p className="hitlab-kicker">REAL HITLAB SIGNAL</p>
        <h2>{activeCopy ? 'The room is already moving.' : 'The room is built for proof.'}</h2>
        <p>
          {activeCopy
            ? 'JG HitLab is not a dead upload box. Records are already moving through Joe Galaxy Radio Rotation, real listening thresholds, structured feedback, and support paths inside JG Universe.'
            : 'JG HitLab is not a dead upload box. Records enter a pipeline built for Radio Rotation, public-safe play signals, structured feedback, and support inside JG Universe.'}
        </p>
      </div>
      <div className="hitlab-proof-signal-grid">
        {PROOF_CARDS.map((card) => {
          const displayCount = countByCard[card.key];
          const displayText = resolveCardCount({ card, proof, displayCount });
          return (
            <article
              key={card.key}
              className="hitlab-proof-signal-card"
              title={card.tooltip || undefined}
            >
              <span>{card.kicker}</span>
              <h3>{resolveCardTitle({ card, proof })}</h3>
              <p>{resolveCardBody({ card, proof })}</p>
              {displayText ? (
                <strong className="hitlab-proof-signal-count">
                  {displayText}
                </strong>
              ) : null}
            </article>
          );
        })}
      </div>
      <HitLabListenerProofSection proof={proof?.listenerProof} onOpenRadio={onOpenRadio} voicePreview={voicePreview} />
      <div className="hitlab-proof-signal-close">
        <p>Placement cannot be bought. The record has to prove it.</p>
        <button
          type="button"
          className="hitlab-primary-btn"
          onClick={onStartIntake}
          disabled={!enabled}
        >
          {ctaLabel}
        </button>
      </div>
    </section>
  );
}
